import React, { useEffect, useState } from 'react';
import { FiSearch, FiRefreshCw } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { exportToExcel, exportToPDF } from '../../utils/export';
import { formatMoney, formatDate } from '../../utils/format';
import PageHeader from '../../components/common/PageHeader';
import Loader from '../../components/common/Loader';
import { showError } from '../../utils/alert';

export default function AccountsBalances() {
  const [accounts, setAccounts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [ledgerData, setLedgerData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  useEffect(() => {
    loadAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAccounts = async () => {
    setLoading(true);
    try {
      const { data: res } = await api.reports.accounts({});
      setAccounts(res.rows || []);
    } catch (err) {
      showError('Failed to load accounts', extractError(err));
    } finally {
      setLoading(false);
    }
  };

  const loadLedger = async (accountId) => {
    setLedgerLoading(true);
    try {
      const { data: res } = await api.reports.ledger({ account_id: accountId });
      setLedgerData(res);
    } catch (err) {
      showError('Failed to load ledger', extractError(err));
    } finally {
      setLedgerLoading(false);
    }
  };

  const types = ['all', ...new Set(accounts.map((a) => a.account_nature).filter(Boolean))];

  const filtered = accounts.filter((a) => {
    if (typeFilter !== 'all' && a.account_nature !== typeFilter) return false;
    if (search && !a.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const ledgerRows = ledgerData ? (ledgerData.entries && ledgerData.entries.data ? ledgerData.entries.data : ledgerData.entries) || [] : [];

  const ledgerCols = [
    { key: 'date', label: 'Date', render: (r) => formatDate(r.date) },
    { key: 'voucher_no', label: 'Voucher No' },
    { key: 'voucher_type', label: 'Type' },
    { key: 'narration', label: 'Narration', render: (r) => (r.narration ? String(r.narration).toUpperCase() : '-') },
    { key: 'debit', label: 'Debit', align: 'right', render: (r) => Number(r.debit) ? formatMoney(r.debit) : '' },
    { key: 'credit', label: 'Credit', align: 'right', render: (r) => Number(r.credit) ? formatMoney(r.credit) : '' },
    { key: 'balance', label: 'Balance', align: 'right', render: (r) => formatMoney(r.balance) },
  ];

  return (
    <div className="page">
      <PageHeader
        title="Accounts Balances"
        subtitle="All accounts with opening balances. Click any account to view its full ledger."
        crumbs={[{ label: 'Reports', to: '/reports' }, { label: 'Accounts Balances' }]}
        actions={null
        }
      />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 3fr', gap: 16 }}>
        <div>
          <div className="card">
            <div className="card-header">
              <div className="card-title">Accounts ({filtered.length})</div>
            </div>
            <div className="card-body" style={{ padding: 12 }}>
              <div className="filters-bar" style={{ marginBottom: 12, flexWrap: 'wrap' }}>
                <div className="topbar-search" style={{ flex: 1, minWidth: 140 }}>
                  <FiSearch className="search-icon" />
                  <input placeholder="Search account..." value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
                <select className="form-control" style={{ width: 130 }} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                  {types.map((t) => <option key={t} value={t}>{t === 'all' ? 'All Types' : t}</option>)}
                </select>
                <button className="btn btn-primary btn-sm" onClick={loadAccounts} title="Refresh"><FiRefreshCw /></button>
              </div>

              {loading ? (
                <Loader text="Loading accounts..." />
              ) : (
                <div style={{ maxHeight: 'calc(100vh - 260px)', overflowY: 'auto' }}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Account</th>
                        <th className="table-num">Closing</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((a) => (
                        <tr
                          key={a.account_id}
                          onClick={() => {
                            setSelected(a);
                            loadLedger(a.account_id);
                          }}
                          style={{
                            cursor: 'pointer',
                            background: selected && selected.account_id === a.account_id ? 'var(--primary-soft)' : undefined,
                            fontWeight: selected && selected.account_id === a.account_id ? 600 : 400,
                          }}
                        >
                          <td>
                            <div>{a.name}</div>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{a.account_nature}</div>
                          </td>
                          <td className="table-num" style={{ color: Number(a.closing_balance) < 0 ? 'var(--danger)' : 'inherit' }}>
                            {formatMoney(a.closing_balance)}
                          </td>
                        </tr>
                      ))}
                      {filtered.length === 0 && (
                        <tr><td colSpan={2} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No accounts found</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        <div>
          <div className="card">
            <div className="card-header">
              <div className="card-title">{selected ? selected.name + ' — Ledger' : 'Ledger'}</div>
              {selected && ledgerData && (
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  Opening: {formatMoney(ledgerData.opening_balance)} &nbsp;|&nbsp; Closing: {formatMoney(ledgerData.closing_balance)}
                </div>
              )}
            </div>
            <div className="card-body" style={{ maxHeight: 'calc(100vh - 180px)', overflowY: 'auto' }}>
              {!selected ? (
                <div className="empty-state" style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--text-muted)' }}>
                  <h5>Select an account from the left to view its full ledger</h5>
                </div>
              ) : ledgerLoading ? (
                <Loader text="Loading ledger..." />
              ) : (
                <>
                  {ledgerData && (
                    <div className="stats-grid" style={{ marginBottom: 14 }}>
                      <div className="stat-card"><div className="stat-icon indigo"><i className="bi bi-cash-stack" /></div><div><div className="stat-value">{formatMoney(ledgerData.opening_balance)}</div><div className="stat-label">Opening Balance</div></div></div>
                      <div className="stat-card"><div className="stat-icon green"><i className="bi bi-box-arrow-in-down" /></div><div><div className="stat-value">{formatMoney(ledgerData.totals?.debit)}</div><div className="stat-label">Total Debit</div></div></div>
                      <div className="stat-card"><div className="stat-icon orange"><i className="bi bi-box-arrow-up" /></div><div><div className="stat-value">{formatMoney(ledgerData.totals?.credit)}</div><div className="stat-label">Total Credit</div></div></div>
                      <div className="stat-card"><div className="stat-icon blue"><i className="bi bi-flag-fill" /></div><div><div className="stat-value">{formatMoney(ledgerData.closing_balance)}</div><div className="stat-label">Closing Balance</div></div></div>
                    </div>
                  )}
                  <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Voucher No</th>
                          <th>Type</th>
                          <th>Narration</th>
                          <th className="table-num">Debit</th>
                          <th className="table-num">Credit</th>
                          <th className="table-num">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ledgerRows.map((r, i) => (
                          <tr key={i}>
                            <td>{formatDate(r.date)}</td>
                            <td>{r.voucher_no}</td>
                            <td>{r.voucher_type}</td>
                            <td>{r.narration ? String(r.narration).toUpperCase() : ''}</td>
                            <td className="table-num">{Number(r.debit) ? formatMoney(r.debit) : ''}</td>
                            <td className="table-num">{Number(r.credit) ? formatMoney(r.credit) : ''}</td>
                            <td className="table-num" style={{ fontWeight: 600, color: Number(r.balance) < 0 ? 'var(--danger)' : 'inherit' }}>{formatMoney(r.balance)}</td>
                          </tr>
                        ))}
                        {ledgerRows.length === 0 && (
                          <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No entries</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
